import React, { useState } from 'react';
import { SearchIcon, DownloadIcon } from 'lucide-react';
export function TransactionSummaryReport() {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [txnType, setTxnType] = useState<'all' | 'p2p' | 'p2m'>('all');
  const [summaryResults, setSummaryResults] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Bank information - hardcoded for this bank portal
  const bankInfo = {
    code: 'AMHRETAA',
    name: 'Amhara Bank S.C.'
  };
  // Sample data for summary report
  const sampleSummaryData = [{
    date: '2023-05-10',
    bankCode: bankInfo.code,
    bankName: bankInfo.name,
    txnType: 'P2P',
    successCount: 156,
    successValue: 785000,
    failedCount: 12,
    failedValue: 45000,
    totalCount: 168,
    totalValue: 830000
  }, {
    date: '2023-05-10',
    bankCode: bankInfo.code,
    bankName: bankInfo.name,
    txnType: 'P2M',
    successCount: 203,
    successValue: 945000,
    failedCount: 18,
    failedValue: 67000,
    totalCount: 221,
    totalValue: 1012000
  }, {
    date: '2023-05-09',
    bankCode: bankInfo.code,
    bankName: bankInfo.name,
    txnType: 'P2P',
    successCount: 142,
    successValue: 690000,
    failedCount: 9,
    failedValue: 32000,
    totalCount: 151,
    totalValue: 722000
  }, {
    date: '2023-05-09',
    bankCode: bankInfo.code,
    bankName: bankInfo.name,
    txnType: 'P2M',
    successCount: 187,
    successValue: 875000,
    failedCount: 14,
    failedValue: 51000,
    totalCount: 201,
    totalValue: 926000
  }, {
    date: '2023-05-08',
    bankCode: bankInfo.code,
    bankName: bankInfo.name,
    txnType: 'P2P',
    successCount: 128,
    successValue: 610000,
    failedCount: 7,
    failedValue: 28000,
    totalCount: 135,
    totalValue: 638000
  }, {
    date: '2023-05-08',
    bankCode: bankInfo.code,
    bankName: bankInfo.name,
    txnType: 'P2M',
    successCount: 172,
    successValue: 820000,
    failedCount: 11,
    failedValue: 42000,
    totalCount: 183,
    totalValue: 862000
  }, {
    date: '2023-05-07',
    bankCode: bankInfo.code,
    bankName: bankInfo.name,
    txnType: 'P2P',
    successCount: 312,
    successValue: 1450000,
    failedCount: 24,
    failedValue: 95000,
    totalCount: 336,
    totalValue: 1545000
  }, {
    date: '2023-05-07',
    bankCode: bankInfo.code,
    bankName: bankInfo.name,
    txnType: 'P2M',
    successCount: 165,
    successValue: 780000,
    failedCount: 13,
    failedValue: 48000,
    totalCount: 178,
    totalValue: 828000
  }];
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Validate date range
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      const diffTime = Math.abs(to.getTime() - from.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 90) {
        alert('Date range cannot exceed 90 days');
        setIsLoading(false);
        return;
      }
      if (from > to) {
        alert('From date must be before To date');
        setIsLoading(false);
        return;
      }
    }
    // Simulate API delay
    setTimeout(() => {
      // Filter sample data based on search criteria
      let results = [...sampleSummaryData];
      // Filter by transaction type if selected
      if (txnType !== 'all') {
        results = results.filter(item => txnType === 'p2p' && item.txnType === 'P2P' || txnType === 'p2m' && item.txnType === 'P2M');
      }
      // Sort by date (newest first) and then by txnType
      results.sort((a, b) => {
        if (a.date !== b.date) {
          return b.date.localeCompare(a.date);
        }
        return a.txnType.localeCompare(b.txnType);
      });
      setSummaryResults(results);
      setHasSearched(true);
      setIsLoading(false);
    }, 800);
  };
  const handleReset = () => {
    setFromDate('');
    setToDate('');
    setTxnType('all');
    setSummaryResults([]);
    setHasSearched(false);
  };
  const handleExport = (format: 'csv' | 'pdf') => {
    // In a real app, you would implement the actual export functionality
    alert(`Exporting as ${format.toUpperCase()}...`);
  };
  // Calculate totals for the footer row
  const calculateTotals = () => {
    return summaryResults.reduce((acc, curr) => {
      acc.successCount += curr.successCount;
      acc.successValue += curr.successValue;
      acc.failedCount += curr.failedCount;
      acc.failedValue += curr.failedValue;
      acc.totalCount += curr.totalCount;
      acc.totalValue += curr.totalValue;
      return acc;
    }, {
      successCount: 0,
      successValue: 0,
      failedCount: 0,
      failedValue: 0,
      totalCount: 0,
      totalValue: 0
    });
  };
  const totals = summaryResults.length > 0 ? calculateTotals() : null;
  return <div className="space-y-6" data-id="element-1344">
      <h1 className="text-2xl font-bold text-[#1A1F36]" data-id="element-1345">
        Transaction Summary Report
      </h1>
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6" data-id="element-1346">
        <form onSubmit={handleSearch} className="space-y-6" data-id="element-1347">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1348">
            <div className="space-y-2" data-id="element-1349">
              <label htmlFor="fromDate" className="block text-sm font-medium text-gray-700" data-id="element-1350">
                From Date
              </label>
              <input id="fromDate" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" required data-id="element-1351" />
            </div>
            <div className="space-y-2" data-id="element-1352">
              <label htmlFor="toDate" className="block text-sm font-medium text-gray-700" data-id="element-1353">
                To Date
              </label>
              <input id="toDate" type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" required data-id="element-1354" />
            </div>
            <div className="space-y-2" data-id="element-1355">
              <label htmlFor="txnType" className="block text-sm font-medium text-gray-700" data-id="element-1356">
                Transaction Type
              </label>
              <div className="flex items-center gap-2" data-id="element-1357">
                <button type="button" onClick={() => setTxnType('all')} className={`px-3 py-2 text-sm rounded-md ${txnType === 'all' ? 'bg-gray-200 text-gray-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1358">
                  All Types
                </button>
                <button type="button" onClick={() => setTxnType('p2p')} className={`px-3 py-2 text-sm rounded-md ${txnType === 'p2p' ? 'bg-blue-100 text-blue-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1359">
                  P2P
                </button>
                <button type="button" onClick={() => setTxnType('p2m')} className={`px-3 py-2 text-sm rounded-md ${txnType === 'p2m' ? 'bg-purple-100 text-purple-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1360">
                  P2M
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-1361">
            <button type="submit" disabled={isLoading} className="px-4 py-2 bg-[#F58220] text-white rounded-md font-medium hover:bg-[#e07619] focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:ring-opacity-50 flex items-center" data-id="element-1362">
              {isLoading ? <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1363">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1364"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1365"></path>
                  </svg>
                  Generating...
                </> : <>
                  <SearchIcon size={16} className="inline mr-2" data-id="element-1366" />
                  Generate Summary
                </>}
            </button>
            <button type="button" onClick={handleReset} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50" data-id="element-1367">
              Reset
            </button>
          </div>
        </form>
      </div>
      {hasSearched && <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-1368">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1369">
            <h3 className="font-medium text-[#1A1F36]" data-id="element-1370">Summary Results</h3>
            {summaryResults.length > 0 && <div className="flex items-center gap-3" data-id="element-1371">
                <button onClick={() => handleExport('csv')} className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1372">
                  <DownloadIcon size={16} className="mr-2" data-id="element-1373" />
                  Export CSV
                </button>
                <button onClick={() => handleExport('pdf')} className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1374">
                  <DownloadIcon size={16} className="mr-2" data-id="element-1375" />
                  Export PDF
                </button>
              </div>}
          </div>
          {summaryResults.length === 0 ? <div className="p-8 text-center" data-id="element-1376">
              <p className="text-gray-500" data-id="element-1377">
                No transaction data found matching your search criteria.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-1378">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1379">
                <thead className="bg-gray-50" data-id="element-1380">
                  <tr data-id="element-1381">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1382">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1383">
                      Bank
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1384">
                      Txn Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1385">
                      Success Count
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1386">
                      Success Value
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1387">
                      Failed Count
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1388">
                      Failed Value
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1389">
                      Total Count
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1390">
                      Total Value
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1391">
                  {summaryResults.map((item, index) => <tr key={index} className="hover:bg-gray-50" data-id="element-1392">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1393">
                        {item.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1394">
                        {item.bankName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1395">
                        <span className={`px-2 py-1 text-xs rounded-full ${item.txnType === 'P2P' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`} data-id="element-1396">
                          {item.txnType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1397">
                        {item.successCount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1398">
                        ETB {item.successValue.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1399">
                        {item.failedCount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1400">
                        ETB {item.failedValue.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1401">
                        {item.totalCount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1402">
                        ETB {item.totalValue.toLocaleString()}
                      </td>
                    </tr>)}
                </tbody>
                {totals && <tfoot className="bg-gray-100" data-id="element-1403">
                    <tr data-id="element-1404">
                      <td colSpan={3} className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900" data-id="element-1405">
                        Totals
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900" data-id="element-1406">
                        {totals.successCount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900" data-id="element-1407">
                        ETB {totals.successValue.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900" data-id="element-1408">
                        {totals.failedCount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900" data-id="element-1409">
                        ETB {totals.failedValue.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900" data-id="element-1410">
                        {totals.totalCount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900" data-id="element-1411">
                        ETB {totals.totalValue.toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>}
              </table>
            </div>}
        </div>}
    </div>;
}